/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Sep 12, 2026 by hoarfroster
 *
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import I18N from '@utils/i18n.base';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastItem {
    id: number;
    message: string;
    type: ToastType;
    duration: number;
}

export interface ToastApi {
    show: (message: string, type?: ToastType, duration?: number) => void;
    success: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
    error: (message: string) => void;
    dismiss: (id: number) => void;
}

/** Auto-dismiss delays: informational toasts are calm, warnings stay longer. */
export const TOAST_DURATIONS: Record<ToastType, number> = {
    info: 4000,
    success: 4000,
    warning: 6000,
    error: 6000,
};

/** Maximum number of toasts kept on screen at once. */
export const MAX_VISIBLE_TOASTS = 3;

/** Pure queue transition: appends a toast and trims the oldest beyond the cap. */
export function pushToast(state: ToastItem[], toast: Omit<ToastItem, 'id'>, seedId: number): ToastItem[] {
    const next = [...state, { ...toast, id: seedId }];
    return next.length > MAX_VISIBLE_TOASTS ? next.slice(next.length - MAX_VISIBLE_TOASTS) : next;
}

const toastVisuals: Record<ToastType, { border: string; icon: string; iconClass: string }> = {
    error: { border: 'border-theme-error/30', icon: 'error', iconClass: 'text-theme-error' },
    success: { border: 'border-theme-success/30', icon: 'check_circle', iconClass: 'text-theme-success' },
    info: { border: 'border-theme-info/30', icon: 'info', iconClass: 'text-theme-info' },
    warning: { border: 'border-theme-warning/30', icon: 'warning', iconClass: 'text-theme-warning' },
};

const ToastContext = createContext<ToastApi>({
    show: () => console.warn('useToast called before ToastProvider is mounted'),
    success: () => console.warn('useToast called before ToastProvider is mounted'),
    info: () => console.warn('useToast called before ToastProvider is mounted'),
    warning: () => console.warn('useToast called before ToastProvider is mounted'),
    error: () => console.warn('useToast called before ToastProvider is mounted'),
    dismiss: () => console.warn('useToast called before ToastProvider is mounted'),
});

/** Access the app-wide toast API. Must be used below `ToastProvider`. */
export function useToast(): ToastApi {
    return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const nextId = useRef(1);

    const dismiss = useCallback((id: number) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    const show = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
        if (!message) return;
        const id = nextId.current++;
        setToasts((current) => pushToast(current, { message, type, duration: duration ?? TOAST_DURATIONS[type] }, id));
    }, []);

    const api = useMemo<ToastApi>(
        () => ({
            show,
            success: (message: string) => show(message, 'success'),
            info: (message: string) => show(message, 'info'),
            warning: (message: string) => show(message, 'warning'),
            error: (message: string) => show(message, 'error'),
            dismiss,
        }),
        [show, dismiss]
    );

    return (
        <ToastContext.Provider value={api}>
            {children}
            <ToastViewport toasts={toasts} onExpire={dismiss} />
        </ToastContext.Provider>
    );
}

function ToastViewport({ toasts, onExpire }: { toasts: ToastItem[]; onExpire: (id: number) => void }) {
    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-3 z-80 flex flex-col items-center gap-2 px-4">
            {toasts.map((toast) => (
                <ToastCard key={toast.id} toast={toast} onExpire={onExpire} />
            ))}
        </div>,
        document.body
    );
}

function ToastCard({ toast, onExpire }: { toast: ToastItem; onExpire: (id: number) => void }) {
    useEffect(() => {
        const timer = window.setTimeout(() => onExpire(toast.id), toast.duration);
        return () => window.clearTimeout(timer);
    }, [toast, onExpire]);

    const visuals = toastVisuals[toast.type];

    return (
        <div
            className={
                'bg-theme-popup-bg/95 animate-toast-in shadow-e3 pointer-events-auto flex items-center rounded-lg border px-3 py-2 backdrop-blur-xl ' +
                visuals.border
            }
            role="status"
            aria-live="polite"
        >
            <span
                className={'material-symbols-rounded mr-2 text-xl leading-none ' + visuals.iconClass}
                aria-hidden="true"
            >
                {visuals.icon}
            </span>
            <p className="text-theme-text m-0 min-w-0 flex-1 truncate text-sm font-medium">{toast.message}</p>
            <button
                type="button"
                aria-label={I18N.app.close}
                className="text-theme-text-muted hover:bg-theme-hover hover:text-theme-text ml-2 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md transition"
                onClick={() => onExpire(toast.id)}
            >
                <span className="material-symbols-rounded text-lg leading-none">close</span>
            </button>
        </div>
    );
}
